import React, { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import {
  CircularProgress,
  Drawer,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import WarningIcon from "@material-ui/icons/Warning";
import HistoryIcon from "@material-ui/icons/History";

import EmptyState from "../../components/EmptyState";
import { StatusBadge } from "../../components/ui";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { getTokens } from "../../styles/tokens";
import { useDate } from "../../hooks/useDate";
import { getTotals, skippedBreakdownText, formatCount } from "./helpers";
import useCampaignTotals from "./useCampaignTotals";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    drawerPaper: {
      width: 560,
      maxWidth: "100vw",
      backgroundColor: t.paper,
      borderTop: "none",
      borderRight: "none",
      borderBottom: "none",
      borderRadius: 0,
      boxShadow: t.shadow.lift,
    },
    // mesmo desenho do título de diálogo: superfície suave com filete
    header: {
      flex: "none",
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 12,
      padding: "18px 22px 14px",
      backgroundColor: t.muted,
      borderBottom: `1px solid ${t.line}`,
    },
    title: {
      margin: 0,
      fontSize: 18,
      fontWeight: 600,
      lineHeight: 1.35,
      letterSpacing: "-0.01em",
      color: t.text,
    },
    subtitle: {
      marginTop: 2,
      color: t.textSecondary,
      fontSize: 13,
      overflowWrap: "anywhere",
    },
    closeButton: {
      color: t.textSecondary,
      marginRight: -6,
    },
    body: {
      flex: "1 1 auto",
      minHeight: 0,
      overflow: "auto",
      ...theme.scrollbarStyles,
    },
    table: {
      minWidth: 480,
      "& .MuiTableCell-root": { verticalAlign: "top" },
    },
    dateCell: {
      whiteSpace: "nowrap",
    },
    totals: {
      display: "flex",
      flexWrap: "wrap",
      gap: "2px 10px",
      fontSize: 12,
      color: t.textSecondary,
      "& b": { fontWeight: 600, color: t.text, fontVariantNumeric: "tabular-nums" },
    },
    totalFailed: {
      "& b": { color: t.semantic.error },
    },
    errorBox: {
      marginTop: 6,
      padding: "6px 8px",
      borderRadius: t.radius.badge,
      backgroundColor: t.status.down.bg,
      color: t.status.down.fg,
      fontSize: 12,
      lineHeight: 1.4,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    },
    warningLine: {
      display: "flex",
      alignItems: "flex-start",
      gap: 6,
      marginTop: 6,
      padding: "6px 8px",
      borderRadius: t.radius.badge,
      backgroundColor: t.status.warn.bg,
      color: t.status.warn.fg,
      fontSize: 12,
      lineHeight: 1.4,
      whiteSpace: "normal",
      wordBreak: "break-word",
    },
    warningIcon: {
      fontSize: 14,
      marginTop: 1,
      flexShrink: 0,
    },
    loading: {
      display: "flex",
      justifyContent: "center",
      padding: theme.spacing(4),
    },
  };
});

// Tom do selo por status da execução (o rótulo continua vindo de billingRules.runStatus.*).
const statusTone = (status) => {
  const s = String(status || "").toLowerCase();
  if (["finished", "completed", "done", "success", "created"].includes(s)) return "success";
  if (["failed", "error"].includes(s)) return "error";
  if (s === "running") return "info";
  return "neutral";
};

const BillingRuleRunsDrawer = ({ open, onClose, rule }) => {
  const classes = useStyles();
  const { datetimeToClient, dateToClient } = useDate();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  // sent/failed are counters of the linked campaign, fetched lazily per run
  const campaignsById = useCampaignTotals(runs.map((r) => r.invoiceCampaignId).filter(Boolean));

  useEffect(() => {
    if (!open || !rule?.id) {
      setRuns([]);
      return undefined;
    }
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/billing-rules/${rule.id}/runs`);
        if (active) setRuns(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [open, rule]);

  const formatStatus = (status) => {
    const key = `billingRules.runStatus.${status}`;
    const translated = i18n.t(key);
    return translated === key ? status || "-" : translated;
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      classes={{ paper: classes.drawerPaper }}
    >
      <div className={classes.header}>
        <div style={{ minWidth: 0 }}>
          <h2 className={classes.title}>{i18n.t("billingRules.runs.title")}</h2>
          {rule && <div className={classes.subtitle}>{rule.name}</div>}
        </div>
        <Tooltip title={i18n.t("billingRules.buttons.close")}>
          <IconButton
            size="small"
            className={classes.closeButton}
            onClick={onClose}
            aria-label={i18n.t("billingRules.buttons.close")}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </div>
      <div className={classes.body}>
        {loading && (
          <div className={classes.loading}>
            <CircularProgress size={24} />
          </div>
        )}
        {!loading && runs.length === 0 && (
          <EmptyState icon={<HistoryIcon />} title={i18n.t("billingRules.runs.empty")} />
        )}
        {!loading && runs.length > 0 && (
          <Table size="small" className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>{i18n.t("billingRules.runs.date")}</TableCell>
                <TableCell>{i18n.t("billingRules.runs.status")}</TableCell>
                <TableCell>{i18n.t("billingRules.runs.totals")}</TableCell>
                <TableCell>{i18n.t("billingRules.runs.campaign")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.map((run) => {
                const campaign = run.invoiceCampaignId
                  ? campaignsById[String(run.invoiceCampaignId)]
                  : null;
                const totals = getTotals(run.totals, campaign);
                const skippedTip = skippedBreakdownText(totals.skippedBreakdown);
                const warnings = Array.isArray(run.warnings)
                  ? run.warnings.filter(Boolean)
                  : [];
                const hasNotes = !!run.error || warnings.length > 0;
                return (
                  <React.Fragment key={run.id}>
                    <TableRow>
                      <TableCell
                        className={classes.dateCell}
                        style={hasNotes ? { borderBottom: "none" } : undefined}
                      >
                        {run.runDate
                          ? String(run.runDate).length > 10
                            ? datetimeToClient(run.runDate)
                            : dateToClient(run.runDate)
                          : "-"}
                      </TableCell>
                      <TableCell style={hasNotes ? { borderBottom: "none" } : undefined}>
                        <StatusBadge color={statusTone(run.status)} label={formatStatus(run.status)} />
                      </TableCell>
                      <TableCell style={hasNotes ? { borderBottom: "none" } : undefined}>
                        <div className={classes.totals}>
                          <span>
                            {i18n.t("billingRules.totals.total")} <b>{totals.total}</b>
                          </span>
                          <span>
                            {i18n.t("billingRules.totals.sent")} <b>{formatCount(totals.sent)}</b>
                          </span>
                          <span className={totals.failed > 0 ? classes.totalFailed : undefined}>
                            {i18n.t("billingRules.totals.failed")} <b>{formatCount(totals.failed)}</b>
                          </span>
                          {totals.pending > 0 && (
                            <span>
                              {i18n.t("billingRules.totals.pending")} <b>{totals.pending}</b>
                            </span>
                          )}
                          <Tooltip title={skippedTip} disableHoverListener={!skippedTip}>
                            <span>
                              {i18n.t("billingRules.totals.skipped")} <b>{totals.skipped}</b>
                            </span>
                          </Tooltip>
                        </div>
                      </TableCell>
                      <TableCell style={hasNotes ? { borderBottom: "none" } : undefined}>
                        {run.invoiceCampaignId ? (
                          <Link component={RouterLink} to="/invoice-campaigns">
                            #{run.invoiceCampaignId}
                          </Link>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                    {hasNotes && (
                      <TableRow>
                        <TableCell colSpan={4} style={{ paddingTop: 0 }}>
                          {run.error && <div className={classes.errorBox}>{run.error}</div>}
                          {warnings.map((w, idx) => (
                            <div
                              key={idx}
                              className={classes.warningLine}
                              title={i18n.t("billingRules.runs.warnings")}
                            >
                              <WarningIcon className={classes.warningIcon} />
                              <span>{String(w)}</span>
                            </div>
                          ))}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </Drawer>
  );
};

export default BillingRuleRunsDrawer;
